// «Кто я» как сотрудник Bitrix — один ответ для экранов, где свой срез должен быть умолчанием.
//
// До этого хука такие экраны (кабинет руководителя, «Мои задания» своего движка) просили
// человека выбрать СЕБЯ из списка в девятьсот фамилий, и комментарии в них честно называли
// это временным положением. Привязка учётки к сотруднику в /api/me при этом уже есть:
// `user.bitrix_user_id`, а при включённых правах — проверенный сервером
// `rights.bitrix_user_id`. Хук просто доносит её до экранов.
//
// Правило: привязка есть — она умолчание. Выбор другого сотрудника остаётся ролям
// head/admin (посмотреть кабинет подчинённого — законная работа руководителя), у роли
// user селект прячется: вопрос «кто вы?» человеку, чья личность известна сессии,
// задавать не за чем.
//
// Это ПОДСКАЗКА интерфейсу, а не защита: сервер сужает выборку сам (web/bff/rights.py),
// и спрятанный селект ничего не охраняет — он убирает лишний вопрос. Поэтому и ошибаемся
// в разрешающую сторону: пока права не пришли (старый BFF, заглушка) — не прячем ничего,
// ровно как остальные подсказки прав в этой CRM.

import { useMemo } from 'react'

import { useMe, useRights } from './hooks'

export interface Self {
  /** /api/me уже отвечён. До этого не решаем ни прятать выбор, ни подставлять человека. */
  loaded: boolean
  /** Сотрудник Bitrix, к которому привязана учётка. null — привязки нет. */
  bitrixUserId: number | null
  /** То же строкой — для селектов и query-параметров. '' — привязки нет. */
  id: string
  /** Роль может выбирать другого сотрудника. false только у роли user. */
  canPickOthers: boolean
}

export function useSelf(): Self {
  const ме = useMe()
  const права = useRights()

  return useMemo(() => {
    // Привязка: при включённых правах берём проверенную сервером, иначе — из учётки.
    // Number(...) > 0, а не «истинность»: «нет связи» в Bitrix однажды приезжало как '0',
    // и truthy-проверка строки сделала бы из него живого сотрудника №0.
    const сырое = права.data?.bitrix_user_id ?? ме.data?.bitrix_user_id
    const номер = Number(сырое ?? 0)
    const bitrixUserId = Number.isFinite(номер) && номер > 0 ? номер : null

    // Роль — из прав (сервер её уже нормализовал), без прав — из учётки как есть.
    // Прячем выбор только по точному 'user': неизвестную роль сервер и так сузит сам,
    // а интерфейс без положительного знания не прячет ничего.
    const роль = права.data?.role ?? ме.data?.role ?? ''

    return {
      loaded: ме.isSuccess,
      bitrixUserId,
      id: bitrixUserId === null ? '' : String(bitrixUserId),
      canPickOthers: роль !== 'user',
    }
  }, [ме.data, ме.isSuccess, права.data])
}
